<div class="min-h-screen bg-gray-50/50 p-6 md:p-12 md:pt-8 font-sans">
    
    <!-- Header Section -->
    <div class="max-w-7xl mx-auto mb-8">
        <a routerLink="/admin-dashboard" class="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-800 transition mb-4 cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Back to Dashboard
        </a>

        <div class="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
                <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight">Product Sales Report</h1>
                <p class="text-gray-500 mt-2">Dynamic breakdown of items sold via invoices and assembly status.</p>
            </div>

            <!-- Stats Cards (Now Dynamic) -->
            <div class="flex gap-4 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
                <div *ngFor="let stat of stats" class="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3 min-w-[180px]">
                    <div [class]="'w-10 h-10 rounded-full flex items-center justify-center text-xl ' + stat.color">
                        {{ stat.icon }}
                    </div>
                    <div>
                        <p class="text-xs text-gray-500 font-bold uppercase">{{ stat.label }}</p>
                        <p class="text-lg font-bold text-gray-900">{{ stat.value }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Filter Tabs -->
    <div class="max-w-7xl mx-auto mb-6">
        <div class="inline-flex bg-white p-1 rounded-xl shadow-sm border border-gray-100">
            <button (click)="setTab('all')" 
                [class.bg-gray-800]="activeTab === 'all'" [class.text-white]="activeTab === 'all'"
                [class.text-gray-500]="activeTab !== 'all'"
                class="px-6 py-2 rounded-lg text-sm font-bold transition-all">All Views</button>
            
            <button (click)="setTab('ready')" 
                [class.bg-orange-500]="activeTab === 'ready'" [class.text-white]="activeTab === 'ready'"
                [class.text-gray-500]="activeTab !== 'ready'"
                class="px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2">
                💎 Ready Sales
            </button>
            
            <button (click)="setTab('assembly')" 
                [class.bg-blue-600]="activeTab === 'assembly'" [class.text-white]="activeTab === 'assembly'"
                [class.text-gray-500]="activeTab !== 'assembly'"
                class="px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2">
                ⚒️ Assembly Line
            </button>
        </div>
    </div>

    <!-- MAIN GRID CONTENT -->
    <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">

        <!-- LEFT SIDE: Ready Products Table (Dynamic) -->
        <div *ngIf="showReady" class="flex flex-col gap-3">
            <h3 class="font-bold text-gray-800 flex items-center gap-2 text-lg">
                <span class="w-1.5 h-6 bg-orange-500 rounded-full"></span> Ready Collection
                <span class="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full font-bold">{{ readySales.length }} Records</span>
            </h3>

            <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="w-full text-left border-collapse">
                        <thead>
                            <tr class="bg-gray-50 border-b border-gray-100 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
                                <th class="px-5 py-4">Item Details</th>
                                <th class="px-5 py-4 text-center">Purity/Metal</th>
                                <th class="px-5 py-4 text-right">Net Wt.</th>
                                <th class="px-5 py-4 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr *ngFor="let item of readySales" class="group hover:bg-orange-50/30 transition-colors duration-150 cursor-pointer">
                                <td class="px-5 py-4">
                                    <p class="font-bold text-gray-800 text-xs group-hover:text-orange-600 transition-colors">{{ item.name }}</p>
                                    <span class="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{{ item.type }}</span>
                                </td>
                                <td class="px-5 py-4 text-center">
                                    <span class="text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded border border-gray-200">
                                        {{ item.purity }}
                                    </span>
                                </td>
                                <td class="px-5 py-4 text-right">
                                    <span class="text-xs text-gray-500 font-medium">{{ item.weight }}</span>
                                </td>
                                <td class="px-5 py-4 text-right">
                                    <span class="text-xs font-bold text-gray-900 font-mono">{{ item.amount }}</span>
                                </td>
                            </tr>
                            <tr *ngIf="readySales.length === 0">
                                <td colspan="4" class="px-5 py-10 text-center text-gray-400 italic">No ready collection sales found.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- RIGHT SIDE: Assembly Line Table (Dynamic) -->
        <div *ngIf="showAssembly" class="flex flex-col gap-3">
            <h3 class="font-bold text-gray-800 flex items-center gap-2 text-lg">
                <span class="w-1.5 h-6 bg-blue-600 rounded-full"></span> Assembly Line Output
                <span class="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-bold">{{ assemblyLine.length }} Batches</span>
            </h3>

            <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="w-full text-left">
                        <thead class="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th class="px-5 py-4 text-[11px] font-bold text-gray-500 uppercase">Stage / Invoice</th>
                                <th class="px-5 py-4 text-[11px] font-bold text-gray-500 uppercase text-right">Input</th>
                                <th class="px-5 py-4 text-[11px] font-bold text-gray-500 uppercase text-right">Est. Val</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr *ngFor="let item of assemblyLine" class="hover:bg-blue-50/20 transition-colors cursor-pointer group">
                                <td class="px-5 py-4">
                                    <div class="flex items-center gap-3">
                                        <span [class]="'w-2 h-2 rounded-full ' + item.color"></span>
                                        <div>
                                            <p class="font-bold text-gray-800 text-xs group-hover:text-blue-600 transition-colors">{{ item.process }}</p>
                                            <p class="text-[9px] text-gray-400 uppercase tracking-wide leading-tight">INV: {{ item.batch }} • {{ item.status }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-5 py-4 text-right">
                                    <span class="text-xs text-gray-600 font-medium">{{ item.input }}</span>
                                </td>
                                <td class="px-5 py-4 text-right">
                                    <span class="text-xs font-bold text-gray-900 font-mono">{{ item.estVal }}</span>
                                </td>
                            </tr>
                            <tr *ngIf="assemblyLine.length === 0">
                                <td colspan="3" class="px-5 py-10 text-center text-gray-400 italic">No assembly line batches found.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

    </div>
</div>